import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import { SpreadsheetMapping, CrudActions, UserPreference, DealDetail } from 'api'
import { TextButtonType, TextButton } from 'view/components/buttons'
import { ModalWindow, ModalFooter, ModalContent, Groupbox } from 'view/components/panels'
import { TextInputField } from 'view/components/inputs'
import { ParseResultPanel } from '../upload'
import { MidTradeEventMappingDialogColumns } from './MidTradeEventMappingDialogColumns'
import { HorizontalLayout } from 'view/styles/constants'
import { Locale, Translation } from 'locale'
import { MidTradeEventSpreadsheetParseResult, parseMidTradeEventSpreadsheet } from './parseMidTradeEventSpreadsheet'

interface MidTradeEventMappingDialogProps {
  title: string
  mapping: SpreadsheetMapping
  parseResult: MidTradeEventSpreadsheetParseResult
  userPreference: UserPreference
  incoming?: boolean
  getDealDetail: (dealId: string) => DealDetail | null
  onClose?: (mappingId?: string) => unknown
}

export const MidTradeEventMappingDialog: React.FunctionComponent<MidTradeEventMappingDialogProps> = props => {
  const { title, userPreference, incoming, getDealDetail, onClose } = props
  const [mapping, setMapping] = useState(props.mapping)
  const [parseResult, setParseResult] = useState(props.parseResult)

  const canSave = true
  const dispatch = useDispatch()

  const handleUpdateColumns = (updatedMapping: SpreadsheetMapping) => {
    setMapping(updatedMapping)
    const updatedParseResult = parseMidTradeEventSpreadsheet(
      parseResult.filename,
      parseResult.rows,
      updatedMapping,
      userPreference,
      !!incoming,
      getDealDetail
    )
    setParseResult(updatedParseResult)
  }

  const handleUpdateName = (name: string) => {
    setMapping({ ...mapping, name })
  }

  const handleSave = () => {
    dispatch(CrudActions.spreadsheetMappingUpsert(mapping))
    onClose?.(mapping.id)
  }

  const handleClose = () => {
    onClose?.()
  }

  return (
    <ModalWindow title={title}>
      <ModalContent>
        <Groupbox title={parseResult.filename}>
          <ParseResultPanel
            parseResult={parseResult}
            itemField="midTradeEventActions"
            successMessage={Translation.SC_MTE_SPREADSHEET_SUCCESS}
            errorMessage={Translation.SC_MTE_SPREADSHEET_ERROR}
            errorNarrative={Translation.SC_UPLOAD_ERROR_NARRATIVE_MAPPING}
          />
        </Groupbox>
        <Groupbox>
          <MidTradeEventMappingDialogColumns
            mapping={mapping}
            parseResult={parseResult}
            incoming={incoming}
            onUpdate={handleUpdateColumns}
          />
        </Groupbox>
        <Groupbox>
          <TextInputField label={Locale.translate(Translation.SC_DISPLAY_NAME)} value={mapping.name} onUpdate={handleUpdateName} />
        </Groupbox>
      </ModalContent>
      <ModalFooter horizontalLayout={HorizontalLayout.SpaceBetween}>
        <TextButton dataTestId={'MappingDialog-TextButton-ButtonCancel'} type={TextButtonType.Tertiary} onClick={handleClose}>
          {Locale.translate(Translation.TC_BUTTON_CANCEL)}
        </TextButton>
        <TextButton
          dataTestId={'MappingDialog-TextButton-ButtonSave'}
          type={TextButtonType.Primary}
          disabled={!canSave}
          onClick={handleSave}>
          {Locale.translate(Translation.TC_BUTTON_SAVE)}
        </TextButton>
      </ModalFooter>
    </ModalWindow>
  )
}
